import { Fragment, useMemo, useRef } from 'react';
import { PencilIcon, TrashIcon } from '@/components/catalog/icons';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  Avatar,
  Badge,
  Button,
  ColumnManager,
  Combobox,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { formatDateTime, orDash } from '@/lib/format';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import type { Contact, ContactGroup, ContactGroupRef } from '@/lib/types';

/*
 * An account's contacts, grouped by the department they sit in, with the
 * primary-contact designation shown and settable on the row that holds it, and
 * the group itself changeable on the row it bands.
 *
 * ⚠️ **The whole list arrives in ONE request and is grouped here.** A group
 * heading over a five-row page is a wrong count rendered confidently —
 * "Finance 2" while eleven finance contacts sit on pages nobody fetched — so
 * the page asks for `perPage: 100` (the entities family's ceiling) and there is
 * no pager. Past 100 the counts become floors and `truncated` says so; see
 * `CountBadge`.
 *
 * ⚠️ **This is a SECOND AFFORDANCE for one writer, not a second writer.**
 * Making somebody primary is `PATCH /companies/:id {primaryContactId}` — the
 * same call the `Primary contact` picker in Account details makes, through the
 * same `saveCompanyField`. There is no `isPrimary` flag on a contact and there
 * must never be one: "exactly one" is structural here because a single-valued
 * column cannot express two, and two writers of one fact drift silently.
 *
 * ⚠️ **The Group cell WRITES THE CONTACT, and the two writes on this row go to
 * different records.** Making somebody primary is `PATCH /companies/:id`;
 * moving them between departments is `PATCH /contacts/:id {contactGroupId}` —
 * so they are gated on different modules (`companies` and `contacts`), and a
 * reader may hold one permission without the other.
 *
 * ⚠️ **Changing the group MOVES THE ROW between the bands above.** Nothing here
 * re-bands anything: `groupContacts` derives the sections from the contacts it
 * is handed on every render, so the page replacing that contact with the record
 * the server returned is what moves the row AND what keeps every heading's
 * count right. **Do not cache a section list, and do not keep a local copy of a
 * row's group** — a count is the one thing on this panel a reader takes as
 * fact, and a stale one states it confidently.
 */

/** What an ungrouped contact's section is called. */
const UNGROUPED_LABEL = 'Ungrouped';

/**
 * One rendered section: a group and the contacts in it, or the ungrouped
 * remainder (`group: null`).
 */
export interface ContactSection {
  group: ContactGroupRef | null;
  contacts: Contact[];
}

/**
 * The contacts, split into sections and ordered the way the master says.
 *
 * Three decisions, each of which a re-implementation gets wrong:
 *
 * 1. **The SECTIONS come from the contacts; the ORDER comes from the master.**
 *    A group nobody in this account belongs to is not rendered as an empty
 *    heading — it is simply absent, which is why there is no "omit empty
 *    groups" branch anywhere below: an empty section is unreachable. The
 *    corollary is the useful half — a contact holding a **retired** group still
 *    gets its own correctly-named section, because reads never filter on
 *    `isActive` and membership, not the master, decides what appears.
 * 2. **Order is decided by the master's `id`, never by its `name`.** `name` is
 *    admin-editable free text and this codebase has been bitten three times by
 *    matching on that kind of column. The master arrives ordered
 *    `sort_order, name` and its INDEX is the rank — so an admin adding
 *    "Marketing" and placing it above Finance moves it here with no code
 *    change, the same way the permission grid survived three changes to the
 *    permission count by rendering from the response.
 * 3. **A group the master does not list sinks below the ones it does, tie-broken
 *    by id** — deterministic, and it never invents a position. `master: null`
 *    (the read failed, or has not answered) is that case for every group: the
 *    sections still render under their real names and only their relative order
 *    falls back to id. A failed master read is not a reason to hide anybody's
 *    contacts, and it is not evidence about which group they are in.
 *
 * **Ungrouped is always last, and on day one it is the whole list**: every
 * contact predating the master has no group and nothing can classify them.
 */
export function groupContacts(contacts: Contact[], master: ContactGroup[] | null): ContactSection[] {
  const rank = new Map<number, number>();
  (master ?? []).forEach((row, index) => rank.set(row.id, index));

  const named = new Map<number, { group: ContactGroupRef; contacts: Contact[] }>();
  const ungrouped: Contact[] = [];

  for (const contact of contacts) {
    const group = contact.contactGroup;
    if (!group) {
      ungrouped.push(contact);
      continue;
    }
    const existing = named.get(group.id);
    if (existing) existing.contacts.push(contact);
    else named.set(group.id, { group, contacts: [contact] });
  }

  const sections: ContactSection[] = [...named.values()].sort((a, b) => {
    /*
     * `MAX_SAFE_INTEGER` rather than `Infinity`: the subtraction below stays a
     * real number, where `Infinity - Infinity` is `NaN`, and a comparator
     * returning `NaN` leaves the array in whatever order the engine was
     * already in — which would look like a working sort on the two seeded
     * groups and shuffle the moment a third one existed.
     */
    const rankA = rank.get(a.group.id) ?? Number.MAX_SAFE_INTEGER;
    const rankB = rank.get(b.group.id) ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return a.group.id - b.group.id;
  });

  if (ungrouped.length > 0) sections.push({ group: null, contacts: ungrouped });
  return sections;
}

function sectionKey(section: ContactSection): string {
  return section.group ? `group-${section.group.id}` : 'ungrouped';
}

function sectionLabel(section: ContactSection): string {
  return section.group ? section.group.name : UNGROUPED_LABEL;
}

/**
 * How many contacts are in this section — **`3+` when the list was truncated**,
 * because then it is a floor and not a total.
 *
 * ⚠️ The bare number would be a confident wrong answer, which is the one thing
 * a grouped view must not produce. `10,000+` on the audit log is the same
 * convention for the same reason: a count the server could not promise is
 * exact prints with the marker rather than pretending.
 */
function CountBadge({ count, truncated }: { count: number; truncated: boolean }) {
  return (
    <span className="shrink-0 text-xs font-normal text-fg-muted tabular">
      {count}
      {truncated && '+'}
    </span>
  );
}

function ContactIdentity({
  contact,
  size,
  isPrimary,
}: {
  contact: Contact;
  size: 'sm' | 'md';
  isPrimary: boolean;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={contact.name} size={size} />
      <div className="min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <TextLink
            to={`/contacts/${contact.id}`}
            className="min-w-0 truncate text-sm font-medium"
          >
            {contact.name}
          </TextLink>
          {/* The word carries the meaning; the tone is a second signal, never
              the only one. Read from `company.primaryContact`, so it agrees
              with the Account-details picker by construction. */}
          {isPrimary && (
            <Badge tone="neutral" variant="outline" className="shrink-0">
              Primary
            </Badge>
          )}
        </span>
        {contact.email && (
          <span className="block text-xs text-fg-muted [overflow-wrap:anywhere]">
            <EmailLink address={contact.email} tone="muted" />
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Why the server will refuse this contact as the primary, or `null` when
 * nothing on the record refuses it.
 *
 * ⚠️ **This narrows NOTHING.** Every contact stays selectable and the 422
 * decides; this only states the reason, so nobody presses a control that comes
 * back refused with no clue why. Over-offering is the safe direction: hiding or
 * disabling these would silently drop a contact the server would in fact have
 * accepted, and the person who met it would report nothing — they would
 * conclude the contact was not set up properly and go and edit a record that
 * was fine.
 *
 * The order mirrors the server's, which checks the name BEFORE the opt-out —
 * see `CompanyInput.primaryContactId`. Its first three refusals are about
 * membership and cannot arise here: every contact shown belongs to this
 * company already.
 *
 * ⚠️ **ONE copy, read by BOTH affordances** — the `Primary contact` picker in
 * Account details (as an option hint) and the row action below. It lives here
 * rather than on the page because the page imports this module and not the
 * reverse. Two copies would agree the day they were written and drift on the
 * first correction, and what would drift is a sentence about why somebody's
 * save was refused.
 */
export function primaryContactRefusal(contact: Contact): string | null {
  const first = contact.firstName?.trim() ?? '';
  const last = contact.lastName?.trim() ?? '';
  /*
   * Both parts unset is the ordinary case, and the API then splits `name` at
   * its LAST space — so a name with no space in it yields one part and no
   * more. With one part filled and the other empty there is nothing to split
   * and the missing half stays missing, which is the state Xero's patch would
   * fill from the PREVIOUS primary.
   */
  const halfNamed =
    first === '' && last === '' ? !/\s/.test(contact.name.trim()) : first === '' || last === '';
  if (halfNamed) return 'Only one name recorded — can’t be the primary contact';
  if (contact.emailOptOut) return 'Opted out of marketing — can’t be the primary contact';
  return null;
}

/**
 * The state of the one make-primary write that may be in flight.
 *
 * `null` is "nothing pressed, or the last press succeeded". Success needs no
 * message: the badge moving to the row that was pressed IS the
 * acknowledgement — and it moves because the RECORD was replaced by the
 * server's answer, not because this list kept a copy of the outcome.
 */
export type PrimaryActionState =
  | null
  | { contactId: number; kind: 'saving' }
  | { contactId: number; kind: 'failed'; message: string };

/**
 * The choices one row's group picker offers: every ACTIVE group, plus the one
 * this contact already holds when the master no longer offers it.
 *
 * ⚠️ **Only active rows are choices, because an inactive id is a 422** —
 * `Contact group {id} does not exist or is not active`, on `path:
 * 'contactGroupId'`. Same rule as the faculty picker, and the opposite of the
 * master read one section up: that one is deliberately UNFILTERED, because a
 * heading has to name a retired group correctly, and a picker must not offer
 * one.
 *
 * The stored value is injected back regardless, so a contact on a since-retired
 * group does not read as ungrouped — a different, real state — and cannot be
 * silently reassigned by a picker that renders blank over it.
 *
 * ⚠️ `records: null` (the master failed, or has not answered) injects the
 * stored value WITHOUT the retired marker: an empty list proves nothing, and
 * accusing a live group of being retired is worse than saying nothing.
 */
function contactGroupOptions(
  records: ContactGroup[] | null,
  stored: ContactGroupRef | null,
): ComboboxOption[] {
  const rows = records ?? [];
  const options: ComboboxOption[] = rows
    .filter((record) => record.isActive)
    .map((record) => ({ value: String(record.id), label: groupLabel(record.id, record.name) }));

  if (!stored) return options;
  if (options.some((option) => option.value === String(stored.id))) return options;

  // Named from the MASTER where the master still carries the row, so a
  // retired-and-renamed group reads by its current name rather than by the
  // stale copy sitting on the contact.
  const fresh = rows.find((record) => record.id === stored.id);
  return [
    {
      value: String(stored.id),
      label: groupLabel(stored.id, fresh ? fresh.name : stored.name),
      ...(rows.length > 0 ? { hint: 'Retired — pick another group to move this contact' } : {}),
    },
    ...options,
  ];
}

/**
 * A group's name, or its id when the name is blank.
 *
 * `name` is `NOT NULL` and admin-entered, so an empty one is reachable and
 * would render an invisible option somebody could select without knowing what
 * they picked.
 */
function groupLabel(id: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? `#${id}` : trimmed;
}

/**
 * The group write in flight on ONE row, or the last one that failed there.
 *
 * ⚠️ **Keyed by contact id, and that is the whole point.** Unlike the primary
 * designation — one company column, so one write at a time — group changes are
 * independent: two rows may save at once and neither may disable or speak for
 * the other. The `value` rides along so a refused choice stays on screen next
 * to the reason it was refused; snapping the control back would leave the
 * person to guess whether anything was sent.
 *
 * **There is no success member, deliberately.** A saved change replaces the
 * CONTACT, which moves the row into its new band under a corrected count — a
 * "Saved" line would be a weaker restatement of something already on screen.
 */
export type ContactGroupAction =
  | { kind: 'saving'; value: string }
  | { kind: 'failed'; value: string; message: string };

/** Per-row group state, by contact id. A missing entry is "nothing in flight". */
export type ContactGroupActions = Record<number, ContactGroupAction | undefined>;

export interface CompanyContactsListProps {
  contacts: Contact[];
  /** The `/contact-groups` master, or `null` when it failed or has not answered. */
  groups: ContactGroup[] | null;
  /**
   * Whether the master read has answered — either way. It separates the two
   * states `groups: null` collapses: a picker that is still loading and one
   * that has nothing to offer are the same absence and different sentences.
   */
  groupsSettled: boolean;
  /** From `company.primaryContact` — the single source both affordances read. */
  primaryContactId: number | null;
  /** `meta.total` exceeded what was fetched, so every count here is a floor. */
  truncated: boolean;
  /**
   * `ability.canUpdate('companies')`. False drops the actions column; the badge
   * stays, and the page states the reason once rather than repeating it down a
   * hundred disabled buttons.
   */
  canUpdate: boolean;
  /**
   * `ability.canUpdate('contacts')` — a DIFFERENT module from `canUpdate`,
   * because the group lives on the contact and the designation on the company.
   *
   * ⚠️ False never removes the Group cell, unlike the Primary column above. The
   * two are not the same decision: an absent action is an action nobody has,
   * where an absent VALUE is a fact hidden from the person who came to read it.
   */
  canUpdateContacts: boolean;
  /** `denialReason(ability, 'update', 'contacts')`, shown when that is false. */
  contactsDenial: string | null;
  action: PrimaryActionState;
  groupActions: ContactGroupActions;
  onMakePrimary: (contact: Contact) => void;
  /** The picked option's value — `''` for "no group", which CLEARS the field. */
  onChangeGroup: (contact: Contact, value: string) => void;
  /** `ability.canDelete('contacts')`. Together with `canUpdateContacts` it decides the actions column. */
  canDeleteContacts: boolean;
  /** Opens the page's one `ContactFormModal` on this contact. */
  onEdit: (contact: Contact) => void;
  /** Opens the page's one delete confirmation on this contact — never deletes by itself. */
  onDelete: (contact: Contact) => void;
}

/**
 * Edit and Delete for one row, shared by the card and the table.
 *
 * Icon-only, ghost and danger, the same pair `/contacts` puts in its own
 * actions column — so a contact offers the same two verbs in the same shape
 * wherever it is listed. The tooltip is the action's name alone; the
 * `aria-label` carries the person, so a screen reader tabbing down the column
 * hears whose row each button belongs to.
 *
 * Delete only ASKS: the confirmation, the request and its refusals all live
 * on the page, which mounts one dialog for the whole list.
 */
function RowActions({
  contact,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      {canEdit && (
        <Tooltip label="Edit">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leadingIcon={<PencilIcon />}
            aria-label={`Edit ${contact.name}`}
            onClick={() => onEdit(contact)}
          />
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip label="Delete">
          <Button
            variant="danger"
            size="sm"
            iconOnly
            leadingIcon={<TrashIcon />}
            aria-label={`Delete ${contact.name}`}
            onClick={() => onDelete(contact)}
          />
        </Tooltip>
      )}
    </div>
  );
}

/** The group cell's contents, shared by the card and the table. */
function GroupCell({
  contact,
  groups,
  groupsSettled,
  canUpdateContacts,
  contactsDenial,
  action,
  onChangeGroup,
}: {
  contact: Contact;
  groups: ContactGroup[] | null;
  groupsSettled: boolean;
  canUpdateContacts: boolean;
  contactsDenial: string | null;
  action: ContactGroupAction | undefined;
  onChangeGroup: (contact: Contact, value: string) => void;
}) {
  const stored = contact.contactGroup;

  if (!canUpdateContacts) {
    /*
     * Read-only means plain text, not a disabled picker: a disabled control
     * reads as something you have not earned the right to use, where text reads
     * as a fact. The reason rides on `title` rather than taking a line under
     * every row.
     */
    return (
      <span
        className="text-sm text-fg-secondary"
        {...(contactsDenial === null ? {} : { title: contactsDenial })}
      >
        {orDash(stored?.name ?? null)}
      </span>
    );
  }

  const options = contactGroupOptions(groups, stored);
  /*
   * The RECORD decides what the cell shows; the attempt overrides it only while
   * that attempt is in flight or after it failed. So a row that saved is
   * showing what the server confirmed, never what was clicked at it.
   */
  const value = action ? action.value : stored ? String(stored.id) : '';
  /*
   * Disabled only when there is genuinely nothing to do: nothing to pick AND no
   * group on this contact to clear. With a group in hand the control still
   * works — taking one away does not need the master.
   *
   * ⚠️ **Two different facts reach that state and they get two different
   * sentences.** `groups: null` after the read settled is "we could not read
   * the list"; a list that answered with no ACTIVE rows is "an administrator
   * has retired them all". Collapsing them into one message would send somebody
   * to retry a request that worked, or to hunt an outage that is a
   * configuration choice.
   */
  const unusable = groupsSettled && options.length === 0;
  const unusableReason = !unusable
    ? null
    : groups === null
      ? 'Contact groups couldn’t be read, so this can’t be changed.'
      : 'No active contact groups, so there is nothing to pick.';

  return (
    <div className="grid gap-1">
      <Combobox
        value={value}
        options={options}
        onChange={(next) => onChangeGroup(contact, next)}
        loading={!groupsSettled}
        disabled={unusable || action?.kind === 'saving'}
        placeholder="No group"
        // `null` clears `contactGroupId`, so "no group" is a real, reachable
        // choice rather than an absence somebody has to work around.
        emptyOptionLabel="No group"
        searchPlaceholder="Filter groups"
        invalid={action?.kind === 'failed'}
      />
      {/* Why the control above is inert — not helper text. A bare `disabled`
          makes an unreadable master, an emptied one and an ordinary value look
          identical, and only one of the three is worth acting on. */}
      {unusableReason !== null && (
        <span className="text-2xs text-fg-muted">{unusableReason}</span>
      )}
      {action?.kind === 'saving' && <span className="text-2xs text-fg-muted">Saving…</span>}
      {action?.kind === 'failed' && (
        <span role="alert" className="text-2xs text-danger [overflow-wrap:anywhere]">
          {action.message}
        </span>
      )}
    </div>
  );
}

/** The action cell's contents, shared by the card and the table. */
function PrimaryAction({
  contact,
  isPrimary,
  action,
  onMakePrimary,
}: {
  contact: Contact;
  isPrimary: boolean;
  action: PrimaryActionState;
  onMakePrimary: (contact: Contact) => void;
}) {
  /*
   * The row that already holds the designation has nothing to offer: sending
   * the value the record already carries is an empty PATCH, which is a 422 —
   * and the badge beside the name already says what its state is.
   */
  if (isPrimary) return null;

  const mine = action?.contactId === contact.id ? action : null;
  const refusal = primaryContactRefusal(contact);

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        size="sm"
        loading={mine?.kind === 'saving'}
        /*
         * A second row is held while one is in flight — not because it is busy
         * but because one designation cannot be two people, and two crossing
         * writes would leave whichever answered last on screen.
         */
        disabled={action?.kind === 'saving' && mine === null}
        aria-label={`Make ${contact.name} the primary contact`}
        onClick={() => onMakePrimary(contact)}
      >
        Make primary
      </Button>
      {/* A refusal, not helper text: it names the state that makes the button
          above come back 422, which the reader cannot otherwise see. */}
      {refusal !== null && <span className="text-2xs text-fg-muted">{refusal}</span>}
      {mine?.kind === 'failed' && (
        <span role="alert" className="text-2xs text-danger [overflow-wrap:anywhere]">
          {mine.message}
        </span>
      )}
    </div>
  );
}

/**
 * The table's columns, in DECLARED order — one array driving the header AND the
 * body row.
 *
 * ⚠️ **Two lists cannot stay in step.** A header written out in JSX beside a
 * body written out in JSX agrees on the day it is written and drifts the first
 * time a column moves or is hidden, and what it produces is a heading over the
 * wrong values — nothing errors, nothing is empty, and the table looks finished.
 *
 * **Group sits beside Title** because both answer "what does this person do
 * here", and because it is the column the bands are drawn from, so it reads next
 * to the heading it moves a row under. That is now the DEFAULT rather than the
 * arrangement: a reader may reorder it, and `applyOrder` keeps a column this
 * table gains later in its declared position rather than appending it.
 *
 * ⚠️ **`primary` is declared only for a reader who can set it.** The column has
 * always been conditional on `canUpdate`; a key left in the list for everyone
 * would be listed by the column manager on a table that never renders it, so its
 * switch would do nothing and read as broken.
 */
const BASE_COLUMN_KEYS = ['contact', 'title', 'group', 'updated'] as const;
const PRIMARY_COLUMN_KEY = 'primary';
/**
 * Edit and Delete. Declared LAST and only for a reader who holds either verb on
 * `contacts` — the same rule as `primary`, for the same reason: a manager
 * listing a column that never renders reads as broken.
 */
const ACTIONS_COLUMN_KEY = 'actions';

/** The stored branch this table's layout lives under — `preferences.columns.<scope>`. */
const COLUMN_SCOPE = 'company-contacts';

const COLUMN_LABELS: Record<string, string> = {
  contact: 'Contact',
  title: 'Title',
  group: 'Group',
  updated: 'Updated',
  primary: 'Primary',
  actions: 'Actions',
};

/** The `w-*` request each column carries. Contact has none and takes the rest. */
const COLUMN_WIDTH_CLASS: Record<string, string | undefined> = {
  title: 'w-48',
  group: 'w-56',
  updated: 'w-44',
  primary: 'w-52',
  actions: 'w-24',
};

/**
 * What each column contributes to the table's minimum width.
 *
 * ⚠️ **These are the two EXISTING floors decomposed, not a re-cut of the column
 * widths.** They sum to **664** across the four base columns and **784** with
 * Primary — byte for byte what this table asked for before the column manager
 * landed — so a reader who has hidden nothing meets exactly the table they met
 * yesterday. `contact` carries the remainder because it is the only column with
 * no `w-*` request of its own; the numbers are a FLOOR and not a sum of rendered
 * widths, which is what the table comment below already says.
 */
const COLUMN_MIN_WIDTH: Record<string, number> = {
  contact: 72,
  title: 192,
  group: 224,
  updated: 176,
  primary: 120,
  // Two `sm` icon buttons and the gap between them, as on `/contacts`.
  actions: 96,
};

/**
 * ⛔ **Three columns that may never be hidden, and only ONE of them is the usual
 * "it holds the row actions" case.**
 *
 * - **`group`** hosts `GroupCell`, which is the ONLY inline group reassignment
 *   anywhere in this app. Hiding it removes a capability, not a column.
 * - **`primary`** hosts `PrimaryAction`, the only "make primary" control on this
 *   surface — the Account-details picker writes the same field, but a reader who
 *   scrolled to the contact list would not find it from here.
 * - **`contact`** is what identifies the row. A table of titles, groups and
 *   timestamps with no names is not a narrower view of this list; it is a
 *   different and useless one.
 * - **`actions`** (added 2026-10-15, a fourth) is the ordinary row-actions case:
 *   Edit and Delete exist nowhere else on this page.
 *
 * ⚠️ **A locked key is still LISTED in the manager, disabled** — so its absence
 * does not read as the feature having lost it.
 *
 * ⚠️ *SUPERSEDED: "The manager's own disabled `title` says 'holds the row
 * actions', which is right about `primary` and approximate about the other two;
 * that string lives in the shared component and is not this file's to change."*
 * `ColumnManager` takes `lockReasonOf` now, so each of the three says why it in
 * particular is locked — see `columnLockReason`. **Two of these three are not
 * the row-actions case at all**, and the default sentence was wrong about both:
 * `group` hosts the only inline group reassignment in the app, and `contact` is
 * what names the row.
 *
 * ⛔ **LOCKED IS NOT PINNED, AND NOTHING HERE IS PINNED.** Locked means this
 * table will not hide or reorder the column; it says nothing about which edge the
 * column sits at. No cell in this file renders `pinned` — the `primary` column is
 * an ordinary last column — so **all five columns are freezable to either edge**,
 * `contact` most usefully of all, and there is no `pinnedKeys` to pass to either
 * `useColumnLayout` or `ColumnManager`.
 */
const LOCKED_COLUMN_KEYS = ['contact', 'group', 'primary', 'actions'] as const;

function columnLabel(key: string): string {
  return COLUMN_LABELS[key] ?? key;
}

/**
 * Why each locked column is locked, in the reader's own terms.
 *
 * ⚠️ **One sentence could not have covered these.** A why-this-is-disabled
 * sentence is one of the four kinds the no-helper-text prohibition explicitly
 * keeps — a bare `disabled` makes a locked column and a broken switch identical —
 * and it is only worth keeping if it is TRUE of the column it sits on. The shared
 * default speaks of row actions, which is right about `primary` and describes
 * neither of the others: hiding `group` would remove a capability that exists
 * nowhere else, and hiding `contact` would leave a table of values describing
 * records nobody can name.
 */
function columnLockReason(key: string): string | undefined {
  switch (key) {
    case 'contact':
      return 'Names the row — without it these are titles and timestamps belonging to nobody';
    case 'group':
      return 'The only place a contact’s department can be changed anywhere in EMERGE';
    case 'primary':
      return 'Holds “Make primary” — the Account details picker writes the same field, but nothing here would lead you to it';
    case 'actions':
      return 'Edit and Delete live only in this column';
    default:
      return undefined;
  }
}

export function CompanyContactsList({
  contacts,
  groups,
  groupsSettled,
  primaryContactId,
  truncated,
  canUpdate,
  canUpdateContacts,
  contactsDenial,
  action,
  groupActions,
  onMakePrimary,
  onChangeGroup,
  canDeleteContacts,
  onEdit,
  onDelete,
}: CompanyContactsListProps) {
  const hasRowActions = canUpdateContacts || canDeleteContacts;
  /*
   * Recomputed every render from the contacts prop, deliberately — see the
   * header. A row whose group just changed lands in its new section, and every
   * heading's count follows, because neither is stored anywhere.
   */
  const sections = groupContacts(contacts, groups);

  // Contact, Title, Group, Updated — plus Primary for a reader who can set it,
  // and Actions for one who can edit or delete a contact.
  const columnKeys = useMemo(
    () => [
      ...BASE_COLUMN_KEYS,
      ...(canUpdate ? [PRIMARY_COLUMN_KEY] : []),
      ...(hasRowActions ? [ACTIONS_COLUMN_KEY] : []),
    ],
    [canUpdate, hasRowActions],
  );
  const layout = useColumnLayout(COLUMN_SCOPE, columnKeys, LOCKED_COLUMN_KEYS);

  /*
   * The header row is MEASURED — a frozen column's offset is the summed rendered
   * width of the columns outside it, and there is no `table-fixed` here, so a
   * `w-*` is a request the content can outvote and the DOM is the only place the
   * real number exists.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, layout.visibleKeys, layout.frozenSideOf);

  const frozenPropsOf = (key: string): { frozen?: 'left' | 'right'; frozenOffset?: number } => {
    const side = layout.frozenSideOf(key);
    if (side === undefined) return {};
    return { frozen: side, frozenOffset: frozenOffsets[key] ?? 0 };
  };

  /*
   * 🔴 **LIVE, never the declared count.** Each section heading is ONE `TD`
   * spanning the table, so a `colSpan` that outlived a hidden column draws a
   * band running past the last cell — and an oversized `colSpan` is not an
   * error, it silently widens the row. A band stopping short is the same failure
   * from the other side. Neither throws and neither fails a build.
   *
   * It cannot reach zero: the layout hook REFUSES to hide the last visible
   * column rather than obeying, and three of these five are locked anyway.
   */
  const columnCount = layout.visibleKeys.length;
  const minWidth = layout.visibleKeys.reduce(
    (total, key) => total + (COLUMN_MIN_WIDTH[key] ?? 0),
    0,
  );

  return (
    <>
      {/* Below md a table cannot fit 320px, so the same rows stack as cards —
          group headings and the badge included, or the grouping would exist on
          one breakpoint only. */}
      <div className="md:hidden">
        {sections.map((section) => (
          <section key={sectionKey(section)} aria-label={sectionLabel(section)}>
            <h3 className="m-0 flex items-center justify-between gap-3 border-b border-border bg-surface-sunken px-4 py-1.5 text-xs font-semibold text-fg-secondary">
              <span className="min-w-0 truncate">{sectionLabel(section)}</span>
              <CountBadge count={section.contacts.length} truncated={truncated} />
            </h3>
            <ul className="m-0 list-none p-0">
              {section.contacts.map((contact) => {
                const isPrimary = primaryContactId === contact.id;
                return (
                  <li key={contact.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    {/* Edit and Delete sit opposite the name, as icons: the card
                        already stacks a picker and possibly "Make primary", and a
                        third row of buttons would bury the person it is about. */}
                    <div className="flex items-start justify-between gap-2">
                      <ContactIdentity contact={contact} size="md" isPrimary={isPrimary} />
                      {hasRowActions && (
                        <div className="-mr-1 shrink-0">
                          <RowActions
                            contact={contact}
                            canEdit={canUpdateContacts}
                            canDelete={canDeleteContacts}
                            onEdit={onEdit}
                            onDelete={onDelete}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate text-xs text-fg-muted">
                        {orDash(contact.title)}
                      </span>
                      <span className="shrink-0 text-xs text-fg-subtle tabular">
                        {formatDateTime(contact.updatedAt)}
                      </span>
                    </div>
                    {/* The group control stacks under the row rather than
                        beside it: a picker and a title on one line at 320px
                        leaves neither readable. */}
                    <div className="mt-2">
                      <GroupCell
                        contact={contact}
                        groups={groups}
                        groupsSettled={groupsSettled}
                        canUpdateContacts={canUpdateContacts}
                        contactsDenial={contactsDenial}
                        action={groupActions[contact.id]}
                        onChangeGroup={onChangeGroup}
                      />
                    </div>
                    {canUpdate && !isPrimary && (
                      <div className="mt-2">
                        <PrimaryAction
                          contact={contact}
                          isPrimary={isPrimary}
                          action={action}
                          onMakePrimary={onMakePrimary}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <div className="hidden md:block">
        {/* The column manager belongs to the TABLE, so it renders inside this
            breakpoint rather than in the page's panel header: the card list
            above has no columns, and a control that does nothing to what is on
            screen is worse than no control. */}
        <div className="flex items-center justify-end px-4 py-2">
          <ColumnManager
            layout={layout}
            labelOf={columnLabel}
            lockedKeys={LOCKED_COLUMN_KEYS}
            lockReasonOf={columnLockReason}
          />
        </div>

        <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
          {/*
           * ONE table with a spanning heading row per group, rather than a
           * table per group: separate tables size their columns independently,
           * so Title and Updated would step sideways at every heading.
           *
           * `min-w` is a FLOOR, not a sum — there is no `table-fixed` here, so
           * the widths are requests the body caps absorb. It follows the VISIBLE
           * set: a floor that still counted a hidden column would leave a
           * horizontal scroll nothing signals over a table that now fits.
           *
           * ⚠️ **It sits on the table's own `style`, and it can only be a
           * NUMBER**: ⛔ a computed `min-w-[…]` class emits NOTHING — Tailwind
           * scans source text, so the class lands in the DOM and does nothing,
           * which looks exactly like a table that chose not to have a floor.
           *
           * ⚠️ *SUPERSEDED: "The floor sits on a wrapper, not on `Table`. `Table`
           * accepts no `style` and is shared by every list page … this wrapper is
           * the one place it fits."* `Table` takes a `style` now, so the floor is
           * declared on the element it describes and the spacer `<div>` is gone.
           */}
          <Table style={{ minWidth }}>
            <THead>
              {/*
               * ⚠️ *SUPERSEDED: "A raw `<tr>` carrying `TR`'s classes verbatim,
               * because `useFrozenOffsets` MEASURES this row's cells and `TR` is
               * a plain function component that forwards no ref."* `TR` takes a
               * `ref` now, so this is the primitive again rather than a second,
               * uncontrolled copy of its three classes — which nothing would have
               * kept in step the day `TR`'s styling moved.
               *
               * The row is still MEASURED, which is why it needs the ref at all:
               * there is no `table-fixed` here, so a `w-*` on a `<th>` is a
               * request the content can outvote and a frozen column's offset —
               * the summed rendered widths outside it — exists only in the DOM.
               */}
              <TR ref={headRowRef}>
                {layout.visibleKeys.map((key) => (
                  <TH
                    key={key}
                    className={COLUMN_WIDTH_CLASS[key]}
                    numeric={key === ACTIONS_COLUMN_KEY}
                    {...frozenPropsOf(key)}
                  >
                    {columnLabel(key)}
                  </TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {sections.map((section) => (
                <Fragment key={sectionKey(section)}>
                  {/*
                   * ⚠️ **The band heading spans and freezes NOTHING.** It is a
                   * single cell across the whole table, so with a column
                   * frozen left the label scrolls away while that column stays
                   * — ordinary for a spanning row, and the alternatives are
                   * worse: `frozen` here would paint a vertical hairline down
                   * the middle of the band, and pinning the inner span would
                   * take `truncate`'s width constraint off a group name that
                   * can be any length.
                   */}
                  <TR>
                    <TD
                      colSpan={columnCount}
                      className="bg-surface-sunken py-1.5 text-xs font-semibold text-fg-secondary"
                    >
                      <span className="flex items-center gap-2">
                        <span className="min-w-0 truncate">{sectionLabel(section)}</span>
                        <CountBadge count={section.contacts.length} truncated={truncated} />
                      </span>
                    </TD>
                  </TR>
                  {section.contacts.map((contact) => {
                    const isPrimary = primaryContactId === contact.id;
                    return (
                      <TR key={contact.id} interactive>
                        {layout.visibleKeys.map((key) => {
                          const frozen = frozenPropsOf(key);
                          switch (key) {
                            case 'contact':
                              return (
                                <TD key={key} {...frozen}>
                                  <ContactIdentity
                                    contact={contact}
                                    size="sm"
                                    isPrimary={isPrimary}
                                  />
                                </TD>
                              );
                            case 'title':
                              return (
                                <TD key={key} {...frozen}>
                                  {orDash(contact.title)}
                                </TD>
                              );
                            case 'group':
                              return (
                                <TD key={key} {...frozen}>
                                  <GroupCell
                                    contact={contact}
                                    groups={groups}
                                    groupsSettled={groupsSettled}
                                    canUpdateContacts={canUpdateContacts}
                                    contactsDenial={contactsDenial}
                                    action={groupActions[contact.id]}
                                    onChangeGroup={onChangeGroup}
                                  />
                                </TD>
                              );
                            case 'updated':
                              return (
                                <TD key={key} className="text-xs text-fg-muted" {...frozen}>
                                  {formatDateTime(contact.updatedAt)}
                                </TD>
                              );
                            /*
                             * Reachable only when `canUpdate` put the key in
                             * the list — the column's condition lives in
                             * `columnKeys` now, so there is no second `&&`
                             * here to fall out of step with it.
                             */
                            case 'primary':
                              return (
                                <TD key={key} {...frozen}>
                                  <PrimaryAction
                                    contact={contact}
                                    isPrimary={isPrimary}
                                    action={action}
                                    onMakePrimary={onMakePrimary}
                                  />
                                </TD>
                              );
                            case 'actions':
                              return (
                                <TD key={key} numeric {...frozen}>
                                  <RowActions
                                    contact={contact}
                                    canEdit={canUpdateContacts}
                                    canDelete={canDeleteContacts}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                  />
                                </TD>
                              );
                            default:
                              return null;
                          }
                        })}
                      </TR>
                    );
                  })}
                </Fragment>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>
    </>
  );
}
